import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ApiCertificat } from '../../models/stat-connexion-api.mapper';
import { EnvoiMailFormPayload, EnvoiMailPayload, ResultatEnvoiMail } from '../envoi-mail-modal/envoi-mail-modal.component';
import { RapportService } from '../../services/rapport.service';

type FiltreStatut = 'tous' | 'connectes' | 'jamaisConnectes';

@Component({
  selector: 'app-certificat-detail-modal',
  templateUrl: './certificat-detail-modal.component.html',
  styleUrls: ['./certificat-detail-modal.component.css'],
})
export class CertificatDetailModalComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() chargementEnCours = false;
  @Input() erreur = false;
  @Input() certificat: ApiCertificat | null = null;

  @Output() fermer = new EventEmitter<void>();

  mailModalVisible = false;
  mailEnvoiEnCours = false;
  mailResultat: ResultatEnvoiMail | null = null;

  recherche = '';
  filtreStatut: FiltreStatut = 'tous';

  exportEchecsEnCours = false;
  progression = 100;      // AJOUT : 100 = aucun envoi en cours
  private intervalle: any; // AJOUT
  constructor(private rapportService: RapportService) { }


  fermerModal(): void {
    this.recherche = '';
    this.filtreStatut = 'tous';
    this.fermer.emit();
  }

  changerFiltre(filtre: FiltreStatut): void {
    this.filtreStatut = filtre;
  }

  get initiales(): string {
    const nom = this.certificat?.certificateName ?? '';
    const mots = nom.trim().split(/\s+/).filter(Boolean);
    if (!mots.length) return '?';
    return (mots[0][0] + (mots[1]?.[0] ?? '')).toUpperCase();
  }

  get tauxConnexion(): number {
    const c = this.certificat;
    if (!c || !c.totalEnrolled) return 0;
    return Math.round((c.totalConnected / c.totalEnrolled) * 1000) / 10;
  }

  get apprenantsFiltres(): any[] {
    const c = this.certificat;
    if (!c || !Array.isArray(c.learners)) return [];

    let liste = c.learners;

    if (this.filtreStatut === 'connectes') {
      liste = liste.filter((l: any) => !l.neverConnected);
    } else if (this.filtreStatut === 'jamaisConnectes') {
      liste = liste.filter((l: any) => l.neverConnected);
    }

    const q = this.recherche.trim().toLowerCase();
    if (q) {
      liste = liste.filter((l: any) =>
        `${l.firstname ?? ''} ${l.lastname ?? ''} ${l.email ?? ''}`.toLowerCase().includes(q)
      );
    }

    return liste;
  }
  exporterExcel(): void {
    if (!this.apprenantsFiltres || this.apprenantsFiltres.length === 0) {
      return;
    }
    const donnees = this.apprenantsFiltres.map(a => ({
      'Prénom': a.firstname || '',
      'Nom': a.lastname || '',
      'Email': a.email || '',
      'Sexe': a.sexe === 'masculin' ? 'Masculin' : a.sexe === 'feminin' ? 'Féminin' : 'N/R',
      'Statut de connexion': a.neverConnected ? 'Jamais connecté' : 'Connecté'
    }));
    const enTetes = ['Prénom', 'Nom', 'Email', 'Sexe', 'Statut de connexion'];
    const lignes = donnees.map(d => [
      `"${d['Prénom']}"`,
      `"${d['Nom']}"`,
      `"${d['Email']}"`,
      `"${d['Sexe']}"`,
      `"${d['Statut de connexion']}"`
    ].join(';'));

    const contenuCsv = '\uFEFF' + [enTetes.join(';'), ...lignes].join('\n');
    const blob = new Blob([contenuCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const lien = document.createElement('a');

    const nomFichier = (this.certificat?.certificateName || 'apprenants')
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, '_');

    lien.setAttribute('href', url);
    lien.setAttribute('download', `liste_apprenants_${nomFichier}.csv`);
    document.body.appendChild(lien);
    lien.click();
    document.body.removeChild(lien);
  }

  ouvrirModalMail(): void {
    this.mailResultat = null;
    this.mailModalVisible = true;
  }

  fermerModalMail(): void {
    this.mailModalVisible = false;
    this.mailEnvoiEnCours = false;
    this.mailResultat = null;
  }
  onEnvoyerMail(payload: EnvoiMailFormPayload): void {
    const destinataires = this.apprenantsFiltres;

    if (!destinataires.length) {
      this.mailResultat = { succes: false, message: 'Aucun destinataire pour ce mail.' };
      return;
    }


    if (this.certificat) {
      const nomCertif = this.certificat.certificateName;
      const payloadComplet: EnvoiMailPayload = {
        ...payload,
        nomCertificat: nomCertif,
        certificatId: this.certificat.certificateId,   // NOUVEAU
        apprenants: destinataires.map((a: any) => ({
          lastname: a.lastname,
          firstname: a.firstname,
          email: a.email,
          certificat: nomCertif
        }))
      };

      this.mailEnvoiEnCours = true;
      this.rapportService.envoyerMailApprenants(payloadComplet).subscribe({
        next: () => {
          this.mailEnvoiEnCours = false;
          this.mailResultat = { succes: true, message: `Envoi lancé pour ${destinataires.length} apprenant(s). Suivez la progression à côté du bouton « Emails en échec ».` };
          this.suivreProgression();   // AJOUT
        },
        error: () => {
          this.mailEnvoiEnCours = false;
          this.mailResultat = { succes: false, message: "Une erreur est survenue lors de l'envoi du mail." };
        },
      });
    }
  }

  // NOUVEAU : quand on change de certificat, on remet le suivi à zéro
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['certificat']) {
      clearInterval(this.intervalle);
      this.progression = 100;
      const id = this.certificat?.certificateId;
      if (id) {
        this.rapportService.getProgression(id).subscribe({
          next: (p) => {
            if (p < 100) this.suivreProgression();
          },
          error: () => { }
        });
      }
    }
  }

  // NOUVEAU : interroge le backend toutes les 1,5 s
  private suivreProgression(): void {
    const id = this.certificat?.certificateId;
    if (!id) return;
    clearInterval(this.intervalle);
    this.progression = 0;
    this.intervalle = setInterval(() => {
      this.rapportService.getProgression(id).subscribe({
        next: (p) => {
          this.progression = p;
          if (p >= 100) clearInterval(this.intervalle);
        },
        error: () => clearInterval(this.intervalle)
      });
    }, 1500);
  }

  // NOUVEAU : arrête le suivi quand le composant est détruit
  ngOnDestroy(): void {
    clearInterval(this.intervalle);
  }

  onFermerResultatMail(): void {
    this.mailResultat = null;
  }

  exporterEmailsEnEchec(): void {
    const id = this.certificat?.certificateId;   // NOUVEAU
    if (!id) return;

    this.exportEchecsEnCours = true;

    this.rapportService.telechargerEmailsEnEchec(id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const lien = document.createElement('a');
        lien.href = url;
        lien.download = 'emails_en_echec.xlsx';
        document.body.appendChild(lien);
        lien.click();
        document.body.removeChild(lien);
        URL.revokeObjectURL(url);
        this.exportEchecsEnCours = false;
      },
      error: (err) => {
        console.error("Impossible de télécharger les emails en échec", err);
        this.exportEchecsEnCours = false;
      },
    });
  }

}